import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { styled } from '@mui/material/styles';
import {
    Box, Typography, Card, CardContent, Grid, Chip, Button,
    Table, TableBody, TableCell, tableCellClasses, TableContainer, TableHead, TableRow,
    Paper, Pagination, Alert, Skeleton, TextField, InputAdornment
} from '@mui/material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import ImageIcon from '@mui/icons-material/Image';
import DescriptionIcon from '@mui/icons-material/Description';
import AssignmentIcon from '@mui/icons-material/Assignment';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import SearchIcon from '@mui/icons-material/Search';
import { getMyOrdersRequest } from '../../redux/modules/externalReferrer/slice';

const StyledTableCell = styled(TableCell)(({ theme }) => ({
    [`&.${tableCellClasses.head}`]: {
        backgroundColor: theme.palette.primary.main,
        color: theme.palette.common.white,
        fontWeight: 600,
    },
    [`&.${tableCellClasses.body}`]: {
        fontSize: 14,
    },
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
    '&:nth-of-type(odd)': {
        backgroundColor: theme.palette.action.hover,
    },
    '&:last-child td, &:last-child th': {
        border: 0,
    },
    '&:hover': {
        backgroundColor: theme.palette.action.selected,
    },
}));

const StatCard = styled(Card)(({ theme }) => ({
    height: '100%',
    transition: 'transform 0.2s, box-shadow 0.2s',
    '&:hover': {
        transform: 'translateY(-2px)',
        boxShadow: theme.shadows[4],
    },
}));

const ExternalDashboard = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { myOrdersRes, loading } = useSelector(state => state.externalReferrer);
    const [page, setPage] = useState(1);
    const [search, setSearch] = useState('');
    const [searchInput, setSearchInput] = useState('');

    useEffect(() => {
        dispatch(getMyOrdersRequest({ page, limit: 10, search }));
    }, [dispatch, page, search]);

    const handleSearch = () => {
        setPage(1);
        setSearch(searchInput);
    };

    const orders = myOrdersRes?.data || [];
    const totalCount = myOrdersRes?.dataCount || 0;

    const formatDate = (date) => {
        if (!date) return '-';
        return new Date(date).toLocaleDateString();
    };

    const getStatusColor = (status) => {
        switch (status) {
            case 'report_complete':
            case 'completed':
                return 'success';
            case 'report_pending':
            case 'pending':
                return 'warning';
            default:
                return 'default';
        }
    };

    const getStatusLabel = (status) => {
        switch (status) {
            case 'report_complete':
                return 'Complete';
            case 'report_pending':
                return 'Pending';
            case 'completed':
                return 'Complete';
            default:
                return status || 'Unknown';
        }
    };

    const getExpirationStatus = (expiresAt) => {
        if (!expiresAt) return null;
        const expires = new Date(expiresAt);
        const now = new Date();
        const daysRemaining = Math.ceil((expires - now) / (1000 * 60 * 60 * 24));

        if (daysRemaining <= 0) {
            return { label: 'Expired', color: 'error' };
        } else if (daysRemaining <= 7) {
            return { label: `${daysRemaining}d left`, color: 'warning' };
        }
        return null;
    };

    // Loading skeleton
    const renderSkeleton = () => (
        <>
            <Grid container spacing={3} mb={4}>
                {[1, 2, 3].map((i) => (
                    <Grid item xs={12} sm={6} md={4} key={i}>
                        <Skeleton variant="rounded" height={120} />
                    </Grid>
                ))}
            </Grid>
            <Skeleton variant="rounded" height={400} />
        </>
    );

    return (
        <Box>
            {/* Page Header */}
            <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 2 }}>
                <Box>
                    <Typography variant="h4" fontWeight={700} color="primary.dark" gutterBottom>
                        My Assigned Orders
                    </Typography>
                    <Typography variant="body1" color="text.secondary">
                        View reports and images shared with you by healthcare providers
                    </Typography>
                </Box>
                <TextField
                    size="small"
                    placeholder="Search by patient name or order ID..."
                    value={searchInput}
                    onChange={(e) => setSearchInput(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter') handleSearch(); }}
                    sx={{ minWidth: 300 }}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <SearchIcon color="action" />
                            </InputAdornment>
                        ),
                    }}
                />
            </Box>

            {loading && orders.length === 0 ? (
                renderSkeleton()
            ) : orders.length === 0 ? (
                <Alert
                    severity="info"
                    sx={{
                        borderRadius: 2,
                        '& .MuiAlert-message': { width: '100%' }
                    }}
                >
                    <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                        No Orders Available
                    </Typography>
                    <Typography variant="body2">
                        When a healthcare provider shares an order with you, it will appear here.
                        You'll be able to view reports and, if granted access, diagnostic images.
                    </Typography>
                </Alert>
            ) : (
                <>
                    {/* Stats Cards */}
                    <Grid container spacing={3} mb={4}>
                        <Grid item xs={12} sm={6} md={4}>
                            <StatCard>
                                <CardContent>
                                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                        <AssignmentIcon color="primary" sx={{ mr: 1 }} />
                                        <Typography color="text.secondary" variant="body2">
                                            Total Orders
                                        </Typography>
                                    </Box>
                                    <Typography variant="h3" fontWeight={700} color="primary.main">
                                        {totalCount}
                                    </Typography>
                                </CardContent>
                            </StatCard>
                        </Grid>
                        <Grid item xs={12} sm={6} md={4}>
                            <StatCard>
                                <CardContent>
                                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                        <CheckCircleIcon color="success" sx={{ mr: 1 }} />
                                        <Typography color="text.secondary" variant="body2">
                                            With Reports
                                        </Typography>
                                    </Box>
                                    <Typography variant="h3" fontWeight={700} color="success.main">
                                        {myOrdersRes?.withReportCount ?? orders.filter(o => o.hasReport).length}
                                    </Typography>
                                </CardContent>
                            </StatCard>
                        </Grid>
                        <Grid item xs={12} sm={6} md={4}>
                            <StatCard>
                                <CardContent>
                                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                        <ImageIcon color="info" sx={{ mr: 1 }} />
                                        <Typography color="text.secondary" variant="body2">
                                            With Image Access
                                        </Typography>
                                    </Box>
                                    <Typography variant="h3" fontWeight={700} color="info.main">
                                        {myOrdersRes?.withImageAccessCount ?? orders.filter(o => o.accessLevel === 'report_and_images' && o.hasImages).length}
                                    </Typography>
                                </CardContent>
                            </StatCard>
                        </Grid>
                    </Grid>

                    {/* Orders Table */}
                    <Box sx={{ overflowX: 'auto' }}>
                        <TableContainer component={Paper} elevation={2} sx={{ borderRadius: 2 }}>
                            <Table sx={{ minWidth: 800 }}>
                                <TableHead>
                                    <TableRow>
                                        <StyledTableCell>Order ID</StyledTableCell>
                                        <StyledTableCell>Patient</StyledTableCell>
                                        <StyledTableCell>Date</StyledTableCell>
                                        <StyledTableCell>Status</StyledTableCell>
                                        <StyledTableCell>Access</StyledTableCell>
                                        <StyledTableCell>Expires</StyledTableCell>
                                        <StyledTableCell align="center">Actions</StyledTableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {orders.map((order) => {
                                        const expStatus = getExpirationStatus(order.expiresAt);
                                        return (
                                            <StyledTableRow key={order._id}>
                                                <StyledTableCell>
                                                    <Typography fontWeight={600}>{order.orderId}</Typography>
                                                </StyledTableCell>
                                                <StyledTableCell>{order.patientName}</StyledTableCell>
                                                <StyledTableCell>{formatDate(order.orderDate)}</StyledTableCell>
                                                <StyledTableCell>
                                                    <Chip
                                                        label={getStatusLabel(order.reportStatus || order.status)}
                                                        size="small"
                                                        color={getStatusColor(order.status)}
                                                        sx={{ fontWeight: 500 }}
                                                    />
                                                </StyledTableCell>
                                                <StyledTableCell>
                                                    <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                                                        <Chip
                                                            icon={<DescriptionIcon sx={{ fontSize: '16px !important' }} />}
                                                            label="Report"
                                                            size="small"
                                                            sx={{
                                                                bgcolor: 'grey.100',
                                                                color: 'text.primary',
                                                                fontWeight: 500,
                                                            }}
                                                        />
                                                        {order.accessLevel === 'report_and_images' && (
                                                            <Chip
                                                                icon={<ImageIcon sx={{ fontSize: '16px !important' }} />}
                                                                label="Images"
                                                                size="small"
                                                                sx={{
                                                                    bgcolor: 'primary.light',
                                                                    color: 'primary.dark',
                                                                    fontWeight: 500,
                                                                }}
                                                            />
                                                        )}
                                                    </Box>
                                                </StyledTableCell>
                                                <StyledTableCell>
                                                    {expStatus ? (
                                                        <Chip
                                                            label={expStatus.label}
                                                            size="small"
                                                            color={expStatus.color}
                                                            sx={{ fontWeight: 500 }}
                                                        />
                                                    ) : (
                                                        <Typography variant="body2" color="text.secondary">
                                                            {formatDate(order.expiresAt)}
                                                        </Typography>
                                                    )}
                                                </StyledTableCell>
                                                <StyledTableCell align="center">
                                                    <Button
                                                        variant="contained"
                                                        size="small"
                                                        startIcon={<VisibilityIcon />}
                                                        onClick={() => navigate(`/external/order/${order._id}`)}
                                                        sx={{
                                                            textTransform: 'none',
                                                            fontWeight: 600,
                                                        }}
                                                    >
                                                        View
                                                    </Button>
                                                </StyledTableCell>
                                            </StyledTableRow>
                                        );
                                    })}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </Box>

                    {/* Pagination */}
                    {totalCount > 10 && (
                        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
                            <Pagination
                                count={Math.ceil(totalCount / 10)}
                                page={page}
                                onChange={(e, value) => setPage(value)}
                                color="primary"
                                size="large"
                            />
                        </Box>
                    )}
                </>
            )}
        </Box>
    );
};

export default ExternalDashboard;
